import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppDispatch } from "..";
import authAPI from "../../apis/auth";

export const handleLogin = createAsyncThunk(
	"auth/login",
	async (data: LoginData) => {
		const resData = await authAPI.login(data);
		return resData;
	}
);

export const handleVerifyLogin = createAsyncThunk(
	"auth/verifyLogin",
	async (data: VerifyLoginData) => {
		const resData = await authAPI.verifyLogin(data);
		localStorage.setItem("token", resData.token);
		return resData;
	}
);

export const handleResendVerification = createAsyncThunk(
	"auth/resendVerification",
	async () => {
		const resData = await authAPI.resendVerification();
		return resData;
	}
);

const slice = createSlice({
	name: "auth",
	initialState: {
		isLoading: false,
		isAuthed: !!localStorage.getItem("token"),
		token: null as string | null,
		pendingVerification: false,
		loginCredentials: null as LoginData | null,
		verificationEmail: null as string | null,
		expiresInMinutes: null as number | null,
	},
	reducers: {
		logout(state) {
			state.isAuthed = false;
			state.token = null;
			state.pendingVerification = false;
			state.loginCredentials = null;
			state.verificationEmail = null;
			state.expiresInMinutes = null;
		},
		clearVerificationState(state) {
			state.pendingVerification = false;
			state.loginCredentials = null;
			state.verificationEmail = null;
			state.expiresInMinutes = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Handle Login
			.addCase(handleLogin.fulfilled, (state, action) => {
				state.isLoading = false;
				state.pendingVerification = true;
				state.loginCredentials = action.meta.arg;
				state.verificationEmail = action.payload.email;
				state.expiresInMinutes = action.payload.expires_in_minutes;
			})
			.addCase(handleLogin.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(handleLogin.rejected, (state) => {
				state.isLoading = false;
			})
			// Handle Verify Login
			.addCase(handleVerifyLogin.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isAuthed = true;
				state.token = action.payload.token;
				state.pendingVerification = false;
				state.loginCredentials = null;
				state.verificationEmail = null;
				state.expiresInMinutes = null;
			})
			.addCase(handleVerifyLogin.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(handleVerifyLogin.rejected, (state) => {
				state.isLoading = false;
			})
			// Handle Resend Verification
			.addCase(handleResendVerification.fulfilled, (state, action) => {
				state.isLoading = false;
				state.verificationEmail = action.payload.email;
			})
			.addCase(handleResendVerification.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(handleResendVerification.rejected, (state) => {
				state.isLoading = false;
			})
			.addDefaultCase((state, action) => {
				if (action.error?.message === "401") {
					state.isAuthed = false;
					state.token = null;
				}
			});
	},
});

const { logout, clearVerificationState } = slice.actions;
export { clearVerificationState };

export function handleLogout() {
	return async (dispatch: AppDispatch) => {
		dispatch(logout());
		const data = await authAPI.logout();
		localStorage.removeItem("token");
		return data;
	};
}

export default slice.reducer;
